import fg from 'fast-glob';
import picomatch from 'picomatch';
import type { TraceMcpConfig } from '../config.js';
import { logger } from '../logger.js';
import { descendantExcludeGlobs } from '../registry.js';
import { isSqliteSidecarPath } from '../utils/db-family.js';
import type { GitignoreMatcher } from '../utils/gitignore.js';
import type { TraceignoreMatcher } from '../utils/traceignore.js';
import type { WorkspaceInfo } from './monorepo.js';

/** Inputs `collectFiles` needs, extracted out of `IndexingPipeline` so the
 *  glob+filter logic can be unit tested and read without the surrounding
 *  class's other lifecycle state. */
export interface FileCollectorParams {
  config: TraceMcpConfig;
  rootPath: string;
  workspaces: WorkspaceInfo[];
  traceignore: TraceignoreMatcher | undefined;
  /**
   * Root `.gitignore` matcher, or undefined when `ignore.gitignore` is off.
   * The watcher has always dropped git-ignored events; the full walk did not,
   * so vendored/generated trees landed in the index and in search results
   * (TRA-468). Same matcher, same stack, one behavior.
   */
  gitignore?: GitignoreMatcher | undefined;
  /** Overridable for tests; defaults to 10_000 (IndexingPipeline.DEFAULT_MAX_FILES). */
  maxFiles: number;
}

/**
 * What `collectFiles` resolved: the indexable paths plus whether the walk hit
 * `security.max_files` and was cut short (TRA-1664). Callers must treat a
 * truncated walk as partial — "not in scope" only means "past the cap" — and
 * surface it (IndexingResult → stats → UI) instead of reporting a whole index.
 */
export interface CollectFilesResult {
  /** Repo-relative file paths to index (at most `limit` entries). */
  files: string[];
  /** True when matches exceeded `maxFiles` and the list was cut to the cap. */
  truncated: boolean;
  /** Total matches before the cap (=== files.length when not truncated). */
  found: number;
  /** The `maxFiles` cap that was applied. */
  limit: number;
}

/**
 * Resolves the set of repo-relative file paths to index for a run, given the
 * project's include/exclude globs, workspace layout, and .traceignore rules.
 *
 * Moved out of `IndexingPipeline.collectFiles` verbatim (2026-07 complexity
 * reduction pass) — behavior must stay byte-identical to the original
 * private method; only `this.*` field reads became explicit parameters.
 */
export async function collectFiles(params: FileCollectorParams): Promise<CollectFilesResult> {
  const { config, rootPath, workspaces, traceignore, gitignore, maxFiles } = params;
  const traceignoreIgnore = traceignore?.toFastGlobIgnore() ?? [];
  // Most-specific registered project owns a path: skip files that live under a
  // registered descendant so an umbrella root doesn't index its child repos
  // into a second DB (the double-index churn behind #209).
  const ignore = [...config.exclude, ...traceignoreIgnore, ...descendantExcludeGlobs(rootPath)];

  // suppressErrors: a path component that matches a directory-shaped glob
  // but is actually a file (e.g. a stray `<ws>/test` FILE meeting the
  // `test/**` include) made fast-glob throw ENOTDIR and abort the whole
  // initial index, bricking the project in 'error' state. Traversal errors
  // (ENOTDIR/EACCES/ELOOP) must skip the entry, not kill indexing.
  // followSymbolicLinks: directory symlinks are not followed by default
  // (config.follow_symlinks) — cycle safety (issue #218, Ansible Molecule's
  // self-referential `roles/<role> -> ../../../` layout), consistent with
  // FileExtractor already skipping symlinked files.
  let entries = await fg(config.include, {
    cwd: rootPath,
    ignore,
    dot: false,
    absolute: false,
    onlyFiles: true,
    suppressErrors: true,
    followSymbolicLinks: config.follow_symlinks,
  });

  const rootedPatterns = config.include.filter((p) => !p.startsWith('**/'));

  // Monorepo / folder-of-projects: the directory-rooted include globs
  // (src/**, app/**, routes/**, ...) only match at the container root, so
  // nested subprojects are missed (e.g. `the/15carats/15carats-laravel/routes`).
  // When workspaces are detected, also discover files with those patterns
  // anchored to each workspace. Global `**/...` patterns already span the whole
  // tree, so only re-anchor the directory-rooted ones.
  if (workspaces.length > 0 && rootedPatterns.length > 0) {
    const wsPatterns = workspaces.flatMap((ws) => rootedPatterns.map((p) => `${ws.path}/${p}`));
    const wsEntries = await fg(wsPatterns, {
      cwd: rootPath,
      ignore,
      dot: false,
      absolute: false,
      onlyFiles: true,
      suppressErrors: true,
      followSymbolicLinks: config.follow_symlinks,
    });
    if (wsEntries.length > 0) {
      const merged = new Set(entries);
      for (const e of wsEntries) merged.add(e);
      entries = [...merged];
    }
  }

  // Workspace/monorepo fallback: the rooted patterns found nothing at the
  // project root, so all code is nested deeper (e.g. root/project/service/src/**,
  // or a repo checked out into a subdirectory of the registered project root).
  // Re-try those patterns with a `**/` prefix. Gated on the ROOTED patterns
  // having zero matches specifically — not on `entries.length === 0` overall,
  // which an unrelated GLOBAL pattern (e.g. `**/*.md` catching a stray
  // top-level README) can make non-zero even while every rooted pattern
  // missed completely, silently skipping this fallback and leaving the index
  // stuck at whatever the global patterns happened to touch.
  if (rootedPatterns.length > 0) {
    const isRootedMatch = picomatch(rootedPatterns, { dot: true });
    const rootedMatched = entries.some((e) => isRootedMatch(e));
    if (!rootedMatched) {
      const deepPatterns = rootedPatterns.map((p) => `**/${p}`);
      const deepEntries = await fg(deepPatterns, {
        cwd: rootPath,
        ignore,
        dot: false,
        absolute: false,
        onlyFiles: true,
        suppressErrors: true,
        followSymbolicLinks: config.follow_symlinks,
      });
      if (deepEntries.length > 0) {
        logger.info(
          { count: deepEntries.length, root: rootPath },
          'Rooted include patterns found nothing at project root — using deep glob patterns',
        );
        const merged = new Set(entries);
        for (const e of deepEntries) merged.add(e);
        entries = [...merged];
      }
    }
  }

  if (traceignore) {
    entries = entries.filter((e) => !traceignore.isIgnored(e));
  }

  // Applied after the deep/workspace fallbacks so those can't smuggle a
  // git-ignored tree back in. ponytail: root .gitignore only — nested
  // .gitignore files are not read, matching GitignoreMatcher's existing
  // contract and the watcher's. Upgrade both together if it ever matters.
  if (gitignore) {
    entries = entries.filter((e) => !gitignore.isIgnored(e));
  }

  // TRA-1943: SQLite sidecars (`*-wal`/`*-shm`/`*-journal`) are engine
  // scratch on a live DB, never source — and they blink in and out as the
  // engine checkpoints, so the full walk keeps handing the extractor paths
  // that die ENOENT on open. Drop them from the walk itself.
  entries = entries.filter((e) => !isSqliteSidecarPath(e));

  if (entries.length > maxFiles) {
    logger.warn(
      { projectRoot: rootPath, found: entries.length, limit: maxFiles },
      'File count exceeds limit — truncating. Increase security.max_files to index more.',
    );
    return {
      files: entries.slice(0, maxFiles),
      truncated: true,
      found: entries.length,
      limit: maxFiles,
    };
  }

  return { files: entries, truncated: false, found: entries.length, limit: maxFiles };
}
